import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';

const s = {
  page: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#eff6ff' },
  card: { background: '#fff', padding: 40, borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', width: '100%', maxWidth: 420 },
  title: { fontSize: 28, fontWeight: 700, color: '#1e3a8a', marginBottom: 8 },
  sub: { color: '#64748b', marginBottom: 28 },
  label: { display: 'block', marginBottom: 6, fontWeight: 600, color: '#374151' },
  input: { width: '100%', padding: '12px 14px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 16, marginBottom: 18, outline: 'none', boxSizing: 'border-box' },
  btn: { width: '100%', padding: '14px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 16, fontWeight: 700, cursor: 'pointer' },
  error: { color: '#ef4444', marginBottom: 16, fontSize: 14 },
  link: { textAlign: 'center', marginTop: 20, color: '#64748b' },
  backBtn: { background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: 14, fontWeight: 600, marginBottom: 16, padding: 0 },
};

export default function Login({ onLogin }) {
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showResend, setShowResend] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMsg, setResendMsg] = useState('');

  // 2FA state
  const [show2FA, setShow2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [twoFACode, setTwoFACode] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setShowResend(false);
    setResendMsg('');
    try {
      const res = await api.post('/auth/login', form);
      if (res.data.requires2FA) {
        setTempToken(res.data.tempToken);
        setShow2FA(true);
        return;
      }
      onLogin(res.data.token, res.data.user);
    } catch (err) {
      const msg = err.response?.data?.error || 'Login failed';
      setError(msg);
      if (msg.toLowerCase().includes('verify your email')) setShowResend(true);
    } finally {
      setLoading(false);
    }
  };

  const verify2FA = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/auth/2fa/verify-login', { tempToken, code: twoFACode });
      onLogin(res.data.token, res.data.user);
    } catch (err) {
      setError(err.response?.data?.error || '2FA verification failed');
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setResendLoading(true);
    setResendMsg('');
    try {
      await api.post('/auth/resend-verification', { email: form.email });
      setResendMsg('Verification email sent! Check your inbox and spam folder.');
    } catch (err) {
      setResendMsg(err.response?.data?.error || 'Failed to resend. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  if (show2FA) {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <div style={s.title}>Two-Factor Auth</div>
          <div style={s.sub}>Enter the 6-digit code from your authenticator app.</div>
          {error && <div style={s.error}>{error}</div>}
          <form onSubmit={verify2FA}>
            <label style={s.label}>Verification Code</label>
            <input
              style={{ ...s.input, letterSpacing: 8, fontSize: 22, textAlign: 'center' }}
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={twoFACode}
              onChange={e => setTwoFACode(e.target.value.replace(/\D/g, ''))}
              placeholder="000000"
              autoFocus
              required
            />
            <button style={s.btn} disabled={loading || twoFACode.length !== 6}>
              {loading ? 'Verifying...' : 'Verify'}
            </button>
          </form>
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <button style={s.backBtn} onClick={() => { setShow2FA(false); setTwoFACode(''); setError(''); }}>
              ← Back to login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.title}>Welcome Back</div>
        <div style={s.sub}>Sign in to manage your loved one's medications</div>
        {error && <div style={s.error}>{error}</div>}
        {showResend && (
          <div style={{ marginBottom: 16 }}>
            {resendMsg
              ? <div style={{ color: resendMsg.includes('Failed') ? '#ef4444' : '#16a34a', fontSize: 13, marginBottom: 8 }}>{resendMsg}</div>
              : null}
            <button onClick={resend} disabled={resendLoading} style={{ ...s.btn, background: '#64748b', fontSize: 14, padding: '10px' }}>
              {resendLoading ? 'Sending...' : 'Resend Verification Email'}
            </button>
          </div>
        )}
        <form onSubmit={submit}>
          <label style={s.label}>Email</label>
          <input style={s.input} type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required />
          <label style={s.label}>Password</label>
          <input style={s.input} type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required />
          <button style={s.btn} disabled={loading}>{loading ? 'Signing in...' : 'Sign In'}</button>
        </form>
        <div style={s.link}><Link to="/forgot-password">Forgot your password?</Link></div>
        <div style={s.link}>Don't have an account? <Link to="/register">Create one free</Link></div>
        <div style={{ textAlign: 'center', marginTop: 24, paddingTop: 20, borderTop: '1px solid #e2e8f0', fontSize: 12, color: '#94a3b8' }}>
          By signing in you agree to our{' '}
          <Link to="/terms" style={{ color: '#2563eb' }}>Terms of Service</Link>
          {' '}and{' '}
          <Link to="/privacy" style={{ color: '#2563eb' }}>Privacy Policy</Link>
        </div>
      </div>
    </div>
  );
}
